import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { api, type OverrideDraft, type OverrideSummary, type Registry } from "@/api.js"
import { Async } from "@/components/Async.js"
import { PageHeader, Section } from "@/components/Page.js"
import { invalidateOverrideViews, RevokeOverride } from "@/components/RevokeOverride.js"
import { DecisionBadge } from "@/components/Status.js"
import { Button } from "@/components/ui/button.js"
import { Card, CardBody } from "@/components/ui/card.js"
import { Input, Label, Textarea } from "@/components/ui/input.js"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.js"
import { Switch } from "@/components/ui/switch.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import { defaultExpiry, endOfLocalDay, inForce, latestExpiry, localDate } from "@/overrides.js"
import { useQuestions } from "@/questions.js"
import { formatDate } from "./Entity.js"

/**
 * Active overrides are a standing list, reviewed rather than discovered. One
 * that forces deferral defeats the only rule the system has, so it must be
 * visible without anyone going looking — and it is created and revoked here,
 * on the list it will appear in, rather than anywhere quieter.
 *
 * `?taxId=` opens the form with the entity filled in: the entity screen links
 * here, because the person who needs an override is usually looking at the
 * decision it will correct.
 */
export function OverridesScreen() {
  const [params] = useSearchParams()
  const prefill = params.get("taxId")
  const [creating, setCreating] = useState(prefill !== null)
  const state = useQuery({ queryKey: ["overrides"], queryFn: api.overrides })
  const registry = useQuery({ queryKey: ["registry"], queryFn: api.registry })

  return (
    <div className="space-y-7">
      <PageHeader
        title="Odstępstwa"
        actions={
          <Button
            variant={creating ? "subtle" : "primary"}
            onClick={() => setCreating((open) => !open)}
          >
            {creating ? "Anuluj" : "Nowe odstępstwo"}
          </Button>
        }
      />

      {creating && (
        <Async state={registry}>
          {(controls) => (
            <CreateOverride
              registry={controls}
              initialTaxId={prefill ?? ""}
              onCreated={() => setCreating(false)}
            />
          )}
        </Async>
      )}

      <Async state={state}>
        {(page) => {
          const now = new Date()
          const active = page.items.filter((item) => inForce(item, now))
          const past = page.items.filter((item) => !inForce(item, now))

          return (
            <div className="space-y-8">
              <Section
                title={`Aktywne (${active.length})`}
                hint="Zawsze na jeden podmiot, z terminem ważności — wygasają same."
              >
                <OverrideTable items={active} mode="active" />
              </Section>
              <Section title="Historia" hint="Odwołane i wygasłe. Nic stąd nie znika.">
                <OverrideTable items={past} mode="past" />
              </Section>
            </div>
          )
        }}
      </Async>
    </div>
  )
}

function OverrideTable({
  items,
  mode,
}: {
  items: readonly OverrideSummary[]
  mode: "active" | "past"
}) {
  const { titleOf } = useQuestions()
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Brak.</p>
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>Podmiot</Th>
          <Th>Pytanie</Th>
          <Th>Kierunek</Th>
          <Th>{mode === "active" ? "Wygasa" : "Koniec"}</Th>
          <Th>Autor</Th>
          <Th>Uzasadnienie</Th>
          {mode === "active" && <Th className="text-right">Akcje</Th>}
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <Tr key={item.overrideId}>
            <Td className="whitespace-nowrap font-mono text-[13px] font-medium">
              <Link
                className="text-foreground no-underline hover:text-primary"
                to={`/entities/${item.country}/${item.taxId}`}
              >
                {item.country}
                {item.taxId}
              </Link>
            </Td>
            <Td className="whitespace-nowrap text-muted-foreground">{titleOf(item.question)}</Td>
            <Td>
              <DecisionBadge decision={item.direction} />
            </Td>
            {mode === "active" ? (
              <Td className="whitespace-nowrap">
                <Expiry validUntil={item.validUntil} />
              </Td>
            ) : (
              <Td className="whitespace-nowrap text-muted-foreground">
                <Ending item={item} />
              </Td>
            )}
            <Td className="text-muted-foreground">{item.author}</Td>
            <Td className="min-w-56">{item.note}</Td>
            {mode === "active" && (
              <Td className="whitespace-nowrap text-right">
                <RevokeOverride overrideId={item.overrideId} />
              </Td>
            )}
          </Tr>
        ))}
      </tbody>
    </Table>
  )
}

/**
 * How an override stopped applying, and by whose hand. A revocation names a
 * person; an expiry names nobody, because that is the point of it — it lapsed
 * on its own, as every override is built to.
 */
function Ending({ item }: { item: OverrideSummary }) {
  if (item.revokedAt !== null) {
    return (
      <>
        odwołane{" "}
        <span className="font-mono text-[13px]" title={item.revokedAt}>
          {formatDate(item.revokedAt).slice(0, 10)}
        </span>
        {item.revokedBy !== null && <> przez {item.revokedBy}</>}
      </>
    )
  }
  return (
    <>
      wygasło{" "}
      <span className="font-mono text-[13px]" title={item.validUntil ?? undefined}>
        {formatDate(item.validUntil).slice(0, 10)}
      </span>
    </>
  )
}

/**
 * When it ends on its own — or that it does not. An open-ended override is the
 * one a reviewer has to notice, so the word is set apart from the dates.
 */
function Expiry({ validUntil }: { validUntil: string | null }) {
  if (validUntil === null) {
    return <span className="font-medium text-review">bezterminowo</span>
  }
  return (
    <span className="font-mono text-[13px]" title={validUntil}>
      {formatDate(validUntil).slice(0, 10)}
    </span>
  )
}

/**
 * The form. What it has to get right is not the fields but the two directions
 * not being equally dangerous: forcing prepayment still sells, forcing
 * deferral defeats the only rule the system has. So it opens on prepayment,
 * and choosing deferral is a deliberate act the row's hint spells out — an
 * override in force substitutes the decision whatever the rules found.
 *
 * **The author is not a field.** The route takes it from the session; the
 * form shows who that is, so the person sees whose name the record will
 * carry. The bounds — how long, how short a note — come from the registry, so
 * the form offers nothing the route then refuses; whatever else the route
 * refuses, it says in a sentence and the sentence is shown.
 *
 * **Open-ended is a switch, off by default.** A date is what makes an override
 * lapse without anyone remembering it; switching that off is a choice the
 * person makes on purpose, and the list then shows the override as one that
 * ends only when somebody revokes it.
 */
function CreateOverride({
  registry,
  initialTaxId,
  onCreated,
}: {
  registry: Registry
  initialTaxId: string
  onCreated: () => void
}) {
  const queryClient = useQueryClient()
  const session = useQuery({ queryKey: ["session"], queryFn: api.session, retry: false })
  const policy = registry.overridePolicy
  const [opened] = useState(() => new Date())

  const [taxId, setTaxId] = useState(initialTaxId)
  const [question, setQuestion] = useState<string | null>(null)
  const asked = question ?? registry.questions[0]?.questionId ?? null
  const [direction, setDirection] = useState<OverrideDraft["direction"]>("PREPAYMENT")
  const [validUntil, setValidUntil] = useState(() => defaultExpiry(opened, policy.maxDurationMs))
  const [openEnded, setOpenEnded] = useState(false)
  const [note, setNote] = useState("")
  const [problem, setProblem] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: api.createOverride,
    onSuccess: () => {
      void invalidateOverrideViews(queryClient)
      onCreated()
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    const expiry = openEnded ? null : endOfLocalDay(validUntil)
    if (taxId.trim().length === 0 || asked === null || (!openEnded && expiry === null)) {
      setProblem("Podaj podmiot i termin ważności.")
      return
    }
    setProblem(null)
    create.mutate({
      taxId: taxId.trim(),
      question: asked,
      direction,
      validUntil: expiry === null ? null : expiry.toISOString(),
      note: note.trim(),
    })
  }

  const refusal = problem ?? (create.error instanceof Error ? create.error.message : null)
  const allow = direction === "ALLOW"

  return (
    <Card>
      <CardBody>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label className="mb-1.5 block" htmlFor="override-taxId">
                Podmiot
              </Label>
              <Input
                id="override-taxId"
                className="font-mono text-[13px]"
                value={taxId}
                onChange={(event) => setTaxId(event.target.value)}
                placeholder="PL5252445767"
              />
            </div>
            <div>
              <Label className="mb-1.5 block" htmlFor="override-question">
                Pytanie
              </Label>
              <Select value={asked ?? ""} onValueChange={setQuestion}>
                <SelectTrigger id="override-question" aria-label="Pytanie">
                  <SelectValue placeholder="Pytanie" />
                </SelectTrigger>
                <SelectContent>
                  {registry.questions.map((entry) => (
                    <SelectItem key={entry.questionId} value={entry.questionId}>
                      {entry.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block" htmlFor="override-direction">
                Kierunek
              </Label>
              <Select
                value={direction}
                onValueChange={(value) => setDirection(value as OverrideDraft["direction"])}
              >
                <SelectTrigger id="override-direction" aria-label="Kierunek">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PREPAYMENT">Przedpłata</SelectItem>
                  <SelectItem value="ALLOW">Odroczenie</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[13px] text-muted-foreground">
                {allow
                  ? "Odroczenie wbrew regułom: obowiązuje, cokolwiek reguły znajdą — także później."
                  : "Przedpłata mimo braku znalezisk. Sprzedaż dochodzi do skutku."}
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label className="mb-1.5 block" htmlFor="override-validUntil">
                Ważne do
              </Label>
              <Input
                id="override-validUntil"
                type="date"
                className="font-mono text-[13px]"
                value={validUntil}
                min={localDate(opened)}
                max={latestExpiry(opened, policy.maxDurationMs)}
                disabled={openEnded}
                onChange={(event) => setValidUntil(event.target.value)}
              />
              <p className="mt-1.5 text-[13px] text-muted-foreground">
                {openEnded
                  ? "Bez terminu: obowiązuje, dopóki ktoś go nie odwoła."
                  : `Do końca dnia, najdalej ${Math.floor(policy.maxDurationMs / 86_400_000)} dni. Wygasa samo.`}
              </p>
            </div>
            <div>
              <Label className="mb-1.5 block" htmlFor="override-openEnded">
                Bezterminowo
              </Label>
              <div className="flex h-8 items-center">
                <Switch
                  id="override-openEnded"
                  checked={openEnded}
                  onCheckedChange={setOpenEnded}
                />
              </div>
              <p className="mt-1.5 text-[13px] text-muted-foreground">
                {allow
                  ? "Odroczenie bez terminu nie wygaśnie samo — zostaje do odwołania."
                  : "Nie wygaśnie samo. Zostaje na liście do odwołania."}
              </p>
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block" htmlFor="override-note">
              Uzasadnienie
            </Label>
            <Textarea
              id="override-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Co wiemy, skąd, i dlaczego rejestry się mylą albo nie wystarczają."
            />
            <p className="mt-1.5 text-[13px] text-muted-foreground">
              Co najmniej {policy.minNoteLength} znaków. To, co ktoś przeczyta w sporze; nie
              opuszcza systemu.
            </p>
          </div>

          {refusal !== null && <p className="text-sm text-critical">{refusal}</p>}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={create.isPending || asked === null}>
              {create.isPending ? "Zapisuję…" : "Zapisz odstępstwo"}
            </Button>
            <p className="text-[13px] text-muted-foreground">
              Autor: {session.data?.email ?? "…"}. Działa od zaraz, od następnej decyzji.
            </p>
          </div>
        </form>
      </CardBody>
    </Card>
  )
}
